/**
 * DigitosCorrigidos — valor de um campo de dígitos em caixas, com as posições que a
 * validação corrigiu destacadas (ADR-030, Req. 1.7; caixas de dígito do protótipo).
 *
 * Vale para IMEI, ICCID, CPF, CNPJ e INEP. A posição é destacada quando o campo está
 * `CORRIGIDO` e o dígito final difere do lido na mesma posição (mesmo tamanho); quando
 * a correção mudou o tamanho (dígito a mais/a menos), nenhuma caixa é destacada e a
 * explicação do campo diz o que mudou. O número inteiro fica num texto acessível único
 * (as caixas são decorativas para leitor de tela).
 */

import { Marcador } from '../services/consulta-types';

/** Campos exibidos em caixas de dígito (protótipo: `kind === "digits"`). */
export const CAMPOS_DIGITOS: ReadonlySet<string> = new Set(['imei', 'iccid', 'cpf_gestor', 'cnpj', 'codigo_inep']);

function digitos(valor: string | null): string {
  return (valor ?? '').replace(/\D+/g, '');
}

/** Posições (base 0) em que o dígito final difere do lido; `[]` se os tamanhos diferem. */
export function posicoesAlteradas(literal: string | null, final: string | null): number[] {
  const lido = digitos(literal);
  const fim = digitos(final);
  if (lido.length === 0 || lido.length !== fim.length) {
    return [];
  }
  const posicoes: number[] = [];
  for (let i = 0; i < fim.length; i += 1) {
    if (lido[i] !== fim[i]) {
      posicoes.push(i);
    }
  }
  return posicoes;
}

export interface DigitosCorrigidosProps {
  readonly valorLiteral: string | null;
  readonly valorFinal: string;
  readonly marcador: Marcador;
}

export function DigitosCorrigidos(props: DigitosCorrigidosProps): JSX.Element {
  const final = digitos(props.valorFinal);
  const alteradas = new Set(props.marcador === 'CORRIGIDO' ? posicoesAlteradas(props.valorLiteral, props.valorFinal) : []);
  return (
    <span className="digitos-wrap">
      <span className="sr-only">{props.valorFinal}</span>
      <span className="digitos" aria-hidden="true" data-testid="digitos">
        {[...final].map((d, i) => (
          <span key={i} className={alteradas.has(i) ? 'digito digito-corrigido' : 'digito'}>
            {d}
          </span>
        ))}
      </span>
    </span>
  );
}
